import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Grid,
  Chip,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Update, 
  AccessTime, 
  CalendarToday,
  TrendingUp,
  Visibility,
  Settings
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { marketAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import EditMarketModal from './EditMarketModal'; // We'll create this

const MarketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [market, setMarket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resultsDialog, setResultsDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [results, setResults] = useState({
    open_digit: '',
    close_digit: '',
    open_panna: '',
    close_panna: ''
  });

  useEffect(() => {
    fetchMarket();
  }, [id]);

  const fetchMarket = async () => {
    try {
      setLoading(true);
      const response = await marketAPI.getMarketById(id);
      setMarket(response.data.market);
      setResults({
        open_digit: response.data.market.open_digit || '',
        close_digit: response.data.market.close_digit || '',
        open_panna: response.data.market.open_panna || '',
        close_panna: response.data.market.close_panna || ''
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch market');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateResults = async () => {
    try {
      await marketAPI.updateMarketResults(id, results);
      setResultsDialog(false);
      fetchMarket();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update results');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this market?')) {
      try {
        await marketAPI.deleteMarket(id);
        navigate('/dashboard/markets');
      } catch (error) {
        setError(error.response?.data?.message || 'Failed to delete market');
      }
    }
  };

  const getMarketStatus = () => {
    if (!market) return 'Loading...';
    
    const now = new Date();
    const currentTime = now.toTimeString().split(' ')[0];
    
    if (market.market_status !== 'active') return 'INACTIVE';
    if (currentTime >= market.open_time && currentTime <= market.close_time) {
      return 'OPEN';
    }
    return 'CLOSED';
  };

  const getTodaySchedule = () => {
    if (!market) return null;
    
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return market.schedule[today];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!market) return <Alert severity="error">Market not found</Alert>;

  const todaySchedule = getTodaySchedule();
  const activeDays = Object.values(market.schedule).filter(day => day.active).length;
  const statusColor = getMarketStatus() === 'OPEN' ? 'success' : 
                     getMarketStatus() === 'CLOSED' ? 'warning' : 'error';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              <TrendingUp />
            </Avatar>
            <Box>
              <Typography variant="h4" gutterBottom>
                {market.market_name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Market ID: {market.id} • UUID: {market.uuid?.substring(0, 8)}...
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setEditDialog(true)}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Edit
            </Button>
            {user.user_type === 'owner' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={handleDelete}
                sx={{ 
                  bgcolor: 'rgba(244,67,54,0.8)', 
                  '&:hover': { bgcolor: 'rgba(244,67,54,1)' }
                }}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Status Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box textAlign="center">
                <Typography variant="h6" gutterBottom>
                  Today ({new Date().toLocaleDateString('en-US', { weekday: 'long' })})
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {market.open_time} - {market.close_time}
                </Typography>
                {/* <Box mt={2} mb={2}>
                  <Chip
                    label={getMarketStatus()}
                    color={statusColor}
                    variant="filled"
                    size="large"
                    sx={{ fontSize: '1.1rem', py: 1, px: 2 }}
                  />
                </Box> */}
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} textAlign="center">
                  <Grid item xs={4}>
                    <Typography variant="h6">{activeDays}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active Days
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6">0</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Games
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6">{market.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Market ID
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Market Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
                Market Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box p={2} bgcolor="rgba(255,255,255,0.02)" borderRadius={2}>
                    <Typography variant="body2" color="text.secondary">Market Name</Typography>
                    <Typography variant="h6">{market.market_name}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box p={2} bgcolor="rgba(255,255,255,0.02)" borderRadius={2}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={market.market_status?.toUpperCase()} 
                      color={market.market_status === 'active' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box p={2} bgcolor="rgba(255,255,255,0.02)" borderRadius={2}>
                    <Typography variant="body2" color="text.secondary">
                      <CalendarToday sx={{ fontSize: 16, mr: 0.5 }} />
                      Created
                    </Typography>
                    <Typography variant="body1">{formatDate(market.createdAt)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box p={2} bgcolor="rgba(255,255,255,0.02)" borderRadius={2}>
                    <Typography variant="body2" color="text.secondary">
                      <Update sx={{ fontSize: 16, mr: 0.5 }} />
                      Last Updated
                    </Typography>
                    <Typography variant="body1">{formatDate(market.updatedAt)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box p={2} bgcolor="rgba(255,255,255,0.02)" borderRadius={2}>
                    <Typography variant="body2" color="text.secondary">UUID</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {market.uuid}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Schedule */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
                Weekly Schedule
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(market.schedule).map(([day, schedule]) => (
                  <Grid item xs={12} key={day}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: schedule.active ? 'success.dark' : 'grey.800',
                        opacity: schedule.active ? 1 : 0.5
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', minWidth: 80 }}>
                        {day}
                      </Typography>
                      {schedule.active ? (
                        <Typography variant="body2">
                          {schedule.open_time} - {schedule.close_time}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Closed
                        </Typography>
                      )}
                      <Chip
                        label={schedule.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={schedule.active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Market Modal */}
      <EditMarketModal
        open={editDialog}
        onClose={() => setEditDialog(false)}
        market={market}
        onSuccess={() => {
          setEditDialog(false);
          fetchMarket();
        }}
      />

      {/* Update Results Dialog */}
      <Dialog open={resultsDialog} onClose={() => setResultsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Update Market Results</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Open Digit"
                type="number"
                value={results.open_digit}
                onChange={(e) => setResults({...results, open_digit: e.target.value})}
                inputProps={{ min: 0, max: 9 }}
                helperText="Single digit (0-9)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Close Digit"
                type="number"
                value={results.close_digit}
                onChange={(e) => setResults({...results, close_digit: e.target.value})}
                inputProps={{ min: 0, max: 9 }}
                helperText="Single digit (0-9)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Open Panna"
                type="number"
                value={results.open_panna}
                onChange={(e) => setResults({...results, open_panna: e.target.value})}
                inputProps={{ min: 100, max: 999 }}
                helperText="Three digits (100-999)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Close Panna"
                type="number"
                value={results.close_panna}
                onChange={(e) => setResults({...results, close_panna: e.target.value})}
                inputProps={{ min: 100, max: 999 }}
                helperText="Three digits (100-999)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateResults} variant="contained">
            Update Results
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default MarketDetails;
